import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Sparkle, Target, Info } from "@phosphor-icons/react";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { useLead } from "../../context/LeadContext";
import { reveal, container } from "../../lib/motion";
import {
  SOCIAL_INTRO, SOCIAL_PACKAGES, SOCIAL_NOTES,
  FUNNEL_NOTE, FUNNEL_INTRO, FUNNEL_BUILDS, FUNNEL_CARE, FUNNEL_CARE_NOTE,
  ESTABLISH_CONVERT, PHOTOGRAPHY, VIDEO_ADDONS, WEBSITES, WEBSITE_CARE,
  EMAIL, YOUTUBE, GRAPHIC_DESIGN, DESIGN_NOTES, DELIVERY, DELIVERY_NOTE,
  PRESENTATION_INTRO, PRESENTATION, PRESENTATION_ADDONS, PRESENTATION_NOTES,
} from "../../data/pricing";

const SectionHead = ({ eyebrow, title, sub }) => (
  <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mb-12 max-w-3xl">
    <motion.span variants={reveal} className="mb-4 block text-xs font-bold uppercase tracking-[0.25em] text-teal">{eyebrow}</motion.span>
    <motion.h2 variants={reveal} className="font-display text-4xl font-black leading-[0.95] tracking-tight text-navy md:text-5xl">{title}</motion.h2>
    {sub && <motion.p variants={reveal} className="mt-4 font-sans text-base leading-relaxed text-zinc-600 md:text-lg">{sub}</motion.p>}
  </motion.div>
);

const PriceCard = ({ p, onBook }) => (
  <motion.div
    variants={reveal}
    className={`group flex h-full flex-col rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1.5 ${p.featured ? "border-navy bg-navy text-white shadow-2xl shadow-navy/20" : "border-zinc-200 bg-white hover:border-teal/40 hover:shadow-xl hover:shadow-teal/10"}`}
    data-testid={`price-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    {p.badge && (
      <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-navy">
        <Sparkle size={12} weight="fill" /> {p.badge}
      </span>
    )}
    <h3 className={`font-display text-xl font-bold tracking-tight ${p.featured ? "text-white" : "text-navy"}`}>{p.name}</h3>
    {p.bestFor && <p className={`mt-1.5 text-sm leading-snug ${p.featured ? "text-white/70" : "text-zinc-500"}`}>{p.bestFor}</p>}

    <div className="mt-4">
      {p.prefix && <span className={`block text-xs font-semibold uppercase tracking-wider ${p.featured ? "text-teal" : "text-zinc-400"}`}>{p.prefix}</span>}
      <div className="flex items-baseline gap-1">
        <span className={`font-display text-4xl font-black tracking-tighter ${p.featured ? "text-white" : "text-navy"}`}>{p.price}</span>
        {p.cadence && <span className={`text-sm font-medium ${p.featured ? "text-white/60" : "text-zinc-400"}`}>{p.cadence}</span>}
      </div>
    </div>

    <ul className="mt-6 flex-1 space-y-2.5">
      {p.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <Check size={16} weight="bold" className="mt-0.5 flex-none text-teal" />
          <span className={`font-sans text-sm leading-snug ${p.featured ? "text-white/85" : "text-zinc-600"}`}>{f}</span>
        </li>
      ))}
    </ul>

    {p.foot && (
      <p className={`mt-4 text-xs leading-snug ${p.featured ? "text-white/55" : "text-zinc-400"}`}>{p.foot}</p>
    )}

    {p.goal && (
      <p className={`mt-5 flex items-start gap-2 rounded-lg p-3 text-xs leading-snug ${p.featured ? "bg-white/10 text-white/80" : "bg-mist text-zinc-600"}`}>
        <Target size={15} weight="bold" className="mt-0.5 flex-none text-teal" /> {p.goal}
      </p>
    )}

    <button
      onClick={onBook}
      className={`mt-6 flex items-center justify-center gap-2 py-3.5 text-xs font-bold uppercase tracking-wider transition-colors ${p.featured ? "bg-teal text-white hover:bg-white hover:text-navy" : "bg-navy text-white hover:bg-teal"}`}
    >
      Get Started <ArrowRight weight="bold" />
    </button>
  </motion.div>
);

const Callout = ({ children, tone = "teal" }) => (
  <div className={`flex items-start gap-3 rounded-2xl border p-5 ${tone === "gold" ? "border-gold/40 bg-gold/10" : "border-teal/30 bg-teal/5"}`}>
    <Info size={20} weight="bold" className={tone === "gold" ? "mt-0.5 flex-none text-gold" : "mt-0.5 flex-none text-teal"} />
    <p className="font-sans text-sm leading-relaxed text-zinc-700">{children}</p>
  </div>
);

export default function PricingPage() {
  const { openLead } = useLead();
  const navigate = useNavigate();
  const book = () => openLead("consultation");

  return (
    <div className="grain min-h-screen overflow-x-hidden bg-white font-sans text-ink">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-mist/60 to-white pb-16 pt-36 md:pt-44">
        <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-teal/10 blur-[130px]" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <motion.div variants={container} initial="hidden" animate="show" className="max-w-3xl">
            <motion.span variants={reveal} className="mb-4 block text-xs font-bold uppercase tracking-[0.25em] text-teal">Services & Pricing</motion.span>
            <motion.h1 variants={reveal} className="font-display text-5xl font-black leading-[0.92] tracking-tight text-navy md:text-7xl">
              Marketing built to <span className="text-teal">establish & expand.</span>
            </motion.h1>
            <motion.p variants={reveal} className="mt-6 max-w-2xl font-sans text-lg leading-relaxed text-zinc-600">
              Transparent, boutique-level packages for social media, funnels, photography, video, websites, email and print — with custom options available. Not sure where to start? Book a free consultation and we'll build a plan around your goals.
            </motion.p>
            <motion.button variants={reveal} onClick={book} className="group mt-8 flex items-center gap-3 bg-teal px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy" data-testid="pricing-hero-book">
              Book a Consultation <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Social Media */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Social Media Management" title="Social Media Management" sub={SOCIAL_INTRO} />
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {SOCIAL_PACKAGES.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>
          <div className="mt-8 space-y-4">
            {SOCIAL_NOTES.map((n, i) => <Callout key={i}>{n}</Callout>)}
          </div>
        </div>
      </section>

      {/* Funnel builds */}
      <section className="bg-mist py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Lead Generation" title="Funnel & Lead Generation" sub={FUNNEL_INTRO} />
          <div className="mb-10"><Callout tone="gold"><strong className="font-semibold text-navy">{FUNNEL_NOTE}</strong></Callout></div>
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-3">
            {FUNNEL_BUILDS.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>

          {/* Optional monthly funnel care */}
          <h3 className="mb-2 mt-16 font-display text-2xl font-black tracking-tight text-navy md:text-3xl">Optional Monthly Funnel Care</h3>
          <p className="mb-8 max-w-2xl font-sans text-sm text-zinc-600">Keep your funnels running and improving after launch. Optional and separate from the one-time build.</p>
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-2">
            {FUNNEL_CARE.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>
          <div className="mt-8"><Callout>{FUNNEL_CARE_NOTE}</Callout></div>
        </div>
      </section>

      {/* Establish & Convert */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-3xl bg-navy p-8 text-white md:p-14"
            data-testid="establish-convert"
          >
            <div className="pointer-events-none absolute -right-10 -top-16 h-72 w-72 rounded-full bg-teal/20 blur-[100px]" />
            <div className="relative grid gap-10 lg:grid-cols-[1fr_1.4fr]">
              <div>
                <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-navy"><Sparkle size={12} weight="fill" /> {ESTABLISH_CONVERT.label}</span>
                <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight md:text-5xl">{ESTABLISH_CONVERT.name}</h2>
                <p className="mt-2 text-sm uppercase tracking-wider text-teal">{ESTABLISH_CONVERT.subtitle}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-black tracking-tighter">{ESTABLISH_CONVERT.price}</span>
                  <span className="text-white/60">{ESTABLISH_CONVERT.cadence}</span>
                </div>
                <p className="mt-1 text-sm font-medium text-teal">+ {ESTABLISH_CONVERT.setup} one-time setup</p>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">{ESTABLISH_CONVERT.description}</p>
                <p className="mt-4 flex items-start gap-2 rounded-lg bg-white/10 p-3 text-xs leading-snug text-white/80"><Target size={15} weight="bold" className="mt-0.5 flex-none text-teal" /> {ESTABLISH_CONVERT.goal}</p>
                <button onClick={book} className="mt-8 flex items-center gap-2 bg-teal px-7 py-4 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-navy">
                  Start Establishing <ArrowRight weight="bold" />
                </button>
              </div>
              <div className="grid gap-8 sm:grid-cols-2">
                {ESTABLISH_CONVERT.groups.map((g) => (
                  <div key={g.title} className={g.title.includes("Phase One") ? "sm:col-span-2" : ""}>
                    <h4 className="mb-3 font-display text-sm font-bold uppercase tracking-wider text-gold">{g.title}</h4>
                    <ul className={`space-y-2 ${g.title.includes("Phase One") ? "sm:columns-2" : ""}`}>
                      {g.items.map((it, i) => (
                        <li key={i} className="flex items-start gap-2.5 break-inside-avoid">
                          <Check size={15} weight="bold" className="mt-0.5 flex-none text-teal" />
                          <span className="text-sm leading-snug text-white/85">{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Photography */}
      <section className="bg-mist py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Jakin Photography" title="Brand Photography & Video" sub="Capture the essence, tell your story — visuals that turn moments into narratives." />
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PHOTOGRAPHY.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>
          <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-6">
            <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-teal">Video Add-Ons</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {VIDEO_ADDONS.map((a) => (
                <div key={a.name} className="flex flex-col rounded-xl bg-mist px-4 py-3">
                  <span className="font-display text-base font-black tracking-tighter text-teal">{a.price}</span>
                  <span className="mt-1 text-xs text-zinc-600">{a.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Websites */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Website Services" title="Websites Built to Convert" />
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {WEBSITES.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>
          <div className="mt-5 grid gap-5 lg:grid-cols-[0.5fr_1fr] lg:items-center">
            <PriceCard p={WEBSITE_CARE} onBook={book} />
            <Callout>{WEBSITE_CARE.note}</Callout>
          </div>
        </div>
      </section>

      {/* Email */}
      <section className="bg-mist py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Email Marketing" title="Email That Nurtures & Converts" />
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-3">
            {EMAIL.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>
        </div>
      </section>

      {/* YouTube */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="YouTube Management" title="Grow Your Channel" />
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-3">
            {YOUTUBE.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>
        </div>
      </section>

      {/* Graphic design */}
      <section className="bg-mist py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Graphic Design" title="Design & Print" />
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {GRAPHIC_DESIGN.map((d) => (
              <motion.div key={d.name} variants={reveal} className="group flex items-center justify-between rounded-xl border border-zinc-200 bg-white px-6 py-5 transition-colors hover:border-teal">
                <span className="font-display text-base font-semibold tracking-tight text-navy">{d.name}</span>
                <span className="font-display text-lg font-black tracking-tighter text-teal">{d.price}</span>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-8 space-y-4">
            {DESIGN_NOTES.map((n, i) => <Callout key={i}>{n}</Callout>)}
          </div>
        </div>
      </section>

      {/* Presentation Design */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Presentation Design" title="Presentation Design" sub={PRESENTATION_INTRO} />
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {PRESENTATION.map((p) => <PriceCard key={p.name} p={p} onBook={book} />)}
          </motion.div>

          <div className="mt-8 rounded-2xl border border-zinc-200 bg-mist p-6">
            <h4 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-teal">Presentation Add-Ons</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {PRESENTATION_ADDONS.map((a) => (
                <div key={a.name} className="flex items-center justify-between rounded-xl bg-white px-4 py-3">
                  <span className="text-sm text-zinc-600">{a.name}</span>
                  <span className="font-display text-sm font-black tracking-tighter text-teal">{a.price}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 space-y-4">
            {PRESENTATION_NOTES.map((n, i) => <Callout key={i}>{n}</Callout>)}
          </div>
        </div>
      </section>

      {/* Delivery terms */}
      <section className="bg-mist py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Project Terms" title="Priority Delivery" />
          <div className="grid gap-5 md:grid-cols-3">
            {DELIVERY.map((d) => (
              <div key={d.name} className="rounded-2xl border border-zinc-200 bg-white p-7">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-lg font-bold tracking-tight text-navy">{d.name}</h3>
                  <span className="font-display text-lg font-black tracking-tighter text-teal">{d.price}</span>
                </div>
                <p className="mt-3 font-sans text-sm leading-relaxed text-zinc-600">{d.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8"><Callout tone="gold">{DELIVERY_NOTE}</Callout></div>
        </div>
      </section>

      {/* General terms */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-zinc-200 bg-mist p-8 md:flex-row md:items-center md:p-10">
            <div className="max-w-2xl">
              <h3 className="font-display text-2xl font-black tracking-tight text-navy md:text-3xl">Service terms & agreement</h3>
              <p className="mt-2 font-sans text-sm leading-relaxed text-zinc-600">Payment, revisions, refunds, third-party costs, portfolio use and our plain-language working agreement — including our all-sales-final policy.</p>
            </div>
            <button onClick={() => navigate("/terms")} data-testid="pricing-terms-link" className="group flex flex-none items-center gap-3 bg-navy px-7 py-4 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-teal">
              Read Terms & Agreement <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-navy py-24 md:py-32">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-[300px] w-[300px] rounded-full bg-teal/20 blur-[120px] md:h-[500px] md:w-[500px]" />
        </div>
        <div className="relative mx-auto max-w-3xl px-5 text-center md:px-10">
          <h2 className="font-display text-4xl font-black leading-[0.95] tracking-tight text-white md:text-6xl">Let's build the right package for you.</h2>
          <p className="mx-auto mt-5 max-w-xl font-sans text-lg leading-relaxed text-white/70">Month-to-month, no long contracts. Book a free consultation and we'll tailor a plan to your goals and budget.</p>
          <button onClick={book} className="group mx-auto mt-9 flex items-center gap-3 bg-teal px-9 py-5 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-navy" data-testid="pricing-cta-book">
            Book a Consultation <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
